<template>
  <div class="page-container budgets-page">
    <portal to="page-name">Orçamentos</portal>
    <div class="card card-page">
      <div class="card-header">
        <h1 class="card-title">Orçamentos</h1>
      </div>
      <div class="card-body">
        <div class="columns">
          <div class="column col-3 col-md-12 col-sm-12 form-group">
            <label for="type" class="form-label">Tipo</label>
            <select id="type" class="form-select" v-model="filter.type">
              <option value="">Todos os tipos</option>
              <option value="forward">Encaminhamento</option>
              <option value="budget">Orçamento</option>
            </select>
          </div>
          <div class="column col-3 col-md-12 col-sm-12 form-group">
            <label for="status" class="form-label">Status</label>
            <select id="status" class="form-select" v-model="filter.status">
              <option value="">Todos os status</option>
              <option v-for="(text, value) in status.statuses"
                      :value="value" :key="value">{{ text }}</option>
            </select>
          </div>
          <div class="column col-sm-12 form-group">
            <label for="search" class="form-label">Nome do paciente ou atendimento</label>
            <div class="input-group">
              <input type="text" id="search" class="form-input"
                     v-model="filter.search" maxlength="100" autocomplete="nope"
                     placeholder="Infome o nome do paciente ou número do atendimento">
              <button class="btn btn-neutral btn-action input-group-btn btn-icon" tabindex="-1">
                <fa-icon :icon="['fal', 'search']"/>
              </button>
            </div>
          </div>
          <div class="column col-auto col-sm-12 mb-2" style="align-self: flex-end">
            <button class="btn btn-primary btn-icon btn-icon-left btn-block"
                    @click="create">
              <fa-icon :icon="['fal', 'plus']"/> Novo orçamento
            </button>
          </div>
        </div>
        <div class="loading mt-2" v-if="loading"></div>
        <template v-else>
          <div class="empty mt-2" v-if="data.items.length === 0">
            <div class="empty-icon">
              <fa-icon :icon="['fal', 'info-circle']" size="3x"/>
            </div>
            <p class="empty-title h5">Orçamentos</p>
            <p class="empty-subtitle">
              Nenhum orçamento encontrado. Verifique os filtros para realizar novas buscas
            </p>
          </div>
          <table class="table table-striped table-hover" v-else>
            <thead>
            <tr>
              <th class="hide-sm" width="40px">#</th>
              <th>Data</th>
              <th>Status</th>
              <th>Paciente</th>
              <th>Número</th>
              <th class="text-right">Total</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(item, i) in data.items" :key="i">
              <td class="hide-sm">{{ i + 1 }}</td>
              <td>
                <router-link :to="editLink(item)">
                  {{ item.createdAt | date('DD/MM/YYYY', true) }}
                </router-link>
              </td>
              <td class="hide-sm">{{ status.getName(item.status) }}</td>
              <td class="hide-sm">{{ item.patient.name }}</td>
              <td class="hide-sm">{{ item.code }}</td>
              <td class="hide-sm text-right">{{ item.total | currency }}</td>
              <td class="text-right">
                <button class="btn btn-sm btn-action btn-icon btn-gray mr-1"
                        :class="{loading: printing && idx === i}"
                        :disabled="printing && idx === i"
                        @click="print(item.id, i)">
                  <fa-icon :icon="['fal', 'print']"></fa-icon>
                </button>
                <button class="btn btn-sm btn-action btn-icon btn-error tooltip"
                        :disabled="item.status !== 'pending'"
                        @click="deleteItem(item.id, i)" data-tooltip="Excluir">
                  <fa-icon :icon="['fal', 'times']"></fa-icon>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
      <div class="card-footer text-center" v-if="data.items.length > 0">
        <button
          v-if="data.hasMore && !loading"
          class="btn btn-primary btn-fixed-width"
          @click="loadMore"
          :disabled="loadingMore"
          :class="{ loading: loadingMore }"
        >Carregar mais</button>
      </div>
    </div>
  </div>
</template>

<script>
import * as status from 'src/data/budget-statuses';
import { PRINT_BUDGET, DELETE_BUDGET } from '@/data/actions/modules/financial';

export default {
  data() {
    return {
      debounceTimeout: null,
      loading: false,
      loadingMore: false,
      printing: false,
      idx: null,
      status,
      filter: {
        type: '',
        status: '',
        search: '',
      },
      data: {
        items: [],
        hasMore: false,
        limit: 30,
        offset: 0,
      },
    };
  },
  watch: {
    filter: {
      handler() {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => this.reload(), 600);
      },
      deep: true,
    },
  },
  computed: {
    canAccessPrintBudget() {
      if (PRINT_BUDGET) {
        return this.$can(PRINT_BUDGET);
      }
      return true;
    },
    canAccessRemoveBudget() {
      if (DELETE_BUDGET) {
        return this.$can(DELETE_BUDGET);
      }
      return true;
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async reload() {
      this.data.offset = 0;
      await this.load();
    },
    async loadMore() {
      this.data.offset += this.data.limit;
      await this.load();
    },
    load() {
      const firstData = this.data.offset === 0;

      if (firstData) {
        this.loading = true;
      } else {
        this.loadingMore = true;
      }

      const params = {
        limit: this.data.limit,
        offset: this.data.offset,
      };

      if (this.filter.type) {
        params.type = this.filter.type;
      }

      if (this.filter.status) {
        params.status = this.filter.status;
      }

      if (this.filter.search.trim().length > 0) {
        params.search = this.filter.search;
      }

      this.$http.get('/budgets', { params })
        .then(({ data }) => {
          this.data.hasMore = data.hasMore;
          this.data.items = firstData ? data.items : [...this.data.items, ...data.items];
        })
        .catch(() => {})
        .then(() => {
          this.loading = false;
          this.loadingMore = false;
        });
    },
    create() {
      this.$router.push('/financial/budgets/create');
    },
    print(id, i) {
      if (!this.canAccessPrintBudget) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return false;
      }
      this.printing = true;
      this.idx = i;
      return this.$file
        .print(`/budgets/${id}/print`)
        .catch(() => {})
        .then(() => {
          this.printing = false;
        });
    },
    deleteItem(id, i) {
      if (!this.canAccessRemoveBudget) {
        this.$toast.show('Acesso não autorizado!', { type: 'error' });
        return;
      }
      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente excluir este orçamento?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: close => this.$http
              .delete(`/budgets/${id}`)
              .then(() => {
                this.data.items.splice(i, 1);
                this.data.total -= 1;
              })
              .catch(() => {})
              .then(() => {
                close();
              }),
          },
        ],
      });
    },
    editLink(item) {
      return `/financial/budgets/${item.id}/edit`;
    },
    paginate({ offset }) {
      this.data.offset = offset;
      this.load();
    },
  },
};
</script>

<style lang="scss">
  @import "./src/assets/scss/_variables.scss";
  .budgets {
  }
</style>
